import { useState, useRef, useCallback } from "react";
import { collection, getDocs, query, where, orderBy, limit, startAfter } from "firebase/firestore";
import { db } from "../firebase";
import { createInviteForBand } from "../bandMembershipService";

const INVITES_PAGE_SIZE = 10;

export function useInviteManagement({ tenantContext, userRole, user, activeBandId, showToast }) {
  const [invites, setInvites] = useState([]);
  const [invitesError, setInvitesError] = useState("");
  const [hasMoreInvites, setHasMoreInvites] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [isGeneratingInvite, setIsGeneratingInvite] = useState(false);
  const lastInviteDocRef = useRef(null);

  const fetchInvites = useCallback(async ({ loadMore = false } = {}) => {
    if (!user || !tenantContext?.bandId || userRole !== "admin") return;
    setInvitesError("");
    try {
      const constraints = [
        where("bandId", "==", tenantContext.bandId),
        orderBy("createdAt", "desc"),
        limit(INVITES_PAGE_SIZE),
      ];
      if (loadMore && lastInviteDocRef.current) {
        constraints.push(startAfter(lastInviteDocRef.current));
      } else {
        lastInviteDocRef.current = null;
      }
      const q = query(collection(db, "invites"), ...constraints);
      const snap = await getDocs(q);
      const newDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setInvites((prev) => (loadMore ? [...prev, ...newDocs] : newDocs));
      lastInviteDocRef.current = snap.docs[snap.docs.length - 1] ?? null;
      setHasMoreInvites(snap.docs.length === INVITES_PAGE_SIZE);
    } catch (err) {
      console.error("Error fetching invites:", err);
      setInvitesError("Could not load invite history.");
    }
  }, [user, tenantContext, userRole]);

  const handleGenerateInvite = useCallback(async () => {
    if (!activeBandId || !user?.uid) return;
    setIsGeneratingInvite(true);
    try {
      const token = await createInviteForBand({ bandId: activeBandId, adminUid: user.uid });
      const joinUrl = window.location.origin + "/join?code=" + token;
      await navigator.clipboard.writeText(joinUrl);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2500);
    } catch (err) {
      console.error("Error generating invite:", err);
      showToast(err?.message || "Failed to generate invite token.");
    } finally {
      setIsGeneratingInvite(false);
    }
  }, [activeBandId, user, showToast]);

  return {
    invites,
    invitesError,
    hasMoreInvites,
    fetchInvites,
    inviteCopied,
    isGeneratingInvite,
    handleGenerateInvite,
  };
}
